/**
 * Keeping a finger number inside the room its staff line has.
 *
 * The staves are never sized for finger numbers. A row of numbers sits in the
 * space the layout already leaves above the treble staff and below the bass
 * staff, and that space ends somewhere: at the edge of what the camera shows,
 * or half-way to the staff line stacked above or below. A number that would
 * pass that limit is moved instead: back toward its staff, and, where that
 * puts it on a note, to the side of the note.
 */

/** A rectangle, y growing downward. */
export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** A row of finger numbers: above the treble staff or below the bass staff. */
export type RowSide = 'above' | 'below';

/** Where a row may be drawn. */
export interface RowRoom {
  /** The y no number of the row may pass, going away from the staff. */
  limit: number;
  /** The y of a row that hugs the staff: the nearest to it a number may come. */
  staffEdge: number;
}

export interface Nudge {
  dx: number;
  dy: number;
}

const NO_NUDGE: Nudge = { dx: 0, dy: 0 };

/** The share of a drawn number's height that is empty above and below its digit. */
const DIGIT_INSET = 0.2;

const assertBox = (box: Box): void => {
  const values = [box.left, box.top, box.right, box.bottom];
  if (!values.every((value) => typeof value === 'number' && !Number.isNaN(value))) {
    throw new TypeError('a box needs four numbers: left, top, right, bottom');
  }
};

const assertFinite = (name: string, value: number): void => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    throw new TypeError(`${name} must be a number`);
  }
};

const moved = (box: Box, { dx, dy }: Nudge): Box => ({
  left: box.left + dx,
  top: box.top + dy,
  right: box.right + dx,
  bottom: box.bottom + dy,
});

const overlaps = (a: Box, b: Box): boolean =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

const grown = (box: Box, by: number): Box => ({
  left: box.left - by,
  top: box.top - by,
  right: box.right + by,
  bottom: box.bottom + by,
});

/** The part of a drawn number its digit covers: text is taller than its digit. */
export const digitBox = (text: Box): Box => {
  assertBox(text);
  const inset = (text.bottom - text.top) * DIGIT_INSET;
  return { ...text, top: text.top + inset, bottom: text.bottom - inset };
};

/**
 * How far a number moves back toward its staff to stay inside the row's room:
 * as far as it passes the limit, and never nearer the staff than a row that
 * hugs it. Positive is downward.
 */
export const pullTowardStaff = (side: RowSide, number: Box, room: RowRoom): number => {
  assertBox(number);
  assertFinite('limit', room.limit);
  assertFinite('staffEdge', room.staffEdge);
  if (side === 'above') {
    const past = room.limit - number.top;
    const spare = room.staffEdge - number.bottom;
    return Math.max(0, Math.min(past, spare));
  }
  const past = number.bottom - room.limit;
  const spare = number.top - room.staffEdge;
  return -Math.max(0, Math.min(past, spare));
};

/**
 * How far sideways a number moves to stand clear of what is drawn where it
 * sits: just past the obstacles it is on or within `clearance` of, to the
 * nearer side that is free. Zero when it is clear already, and when neither
 * side is free.
 */
export const sidewaysNudge = (
  number: Box,
  obstacles: ReadonlyArray<Box>,
  clearance: number,
): number => {
  assertBox(number);
  assertFinite('clearance', clearance);
  const isClearAt = (dx: number): boolean => {
    const keptClear = grown(moved(number, { dx, dy: 0 }), clearance);
    return !obstacles.some((obstacle) => overlaps(keptClear, obstacle));
  };
  const inTheWay = obstacles.filter((obstacle) => overlaps(grown(number, clearance), obstacle));
  if (inTheWay.length === 0) return 0;
  const toTheLeft = Math.min(...inTheWay.map((obstacle) => obstacle.left)) - clearance;
  const toTheRight = Math.max(...inTheWay.map((obstacle) => obstacle.right)) + clearance;
  const nearestFirst = [toTheLeft - number.right, toTheRight - number.left].sort(
    (a, b) => Math.abs(a) - Math.abs(b),
  );
  const free = nearestFirst.filter(isClearAt);
  return free.length > 0 ? free[0] : 0;
};

/**
 * Where a number goes when its row has only `room`: back toward the staff as
 * far as it passes the limit and then, if that puts it on a note, to the side.
 * A number that fits where its row put it does not move.
 */
export const nudgeIntoRoom = (
  side: RowSide,
  number: Box,
  room: RowRoom,
  obstacles: ReadonlyArray<Box>,
  clearance: number,
): Nudge => {
  const dy = pullTowardStaff(side, digitBox(number), room);
  if (dy === 0) return NO_NUDGE;
  const dx = sidewaysNudge(digitBox(moved(number, { dx: 0, dy })), obstacles, clearance);
  return { dx, dy };
};
